import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Sankey, Tooltip, ResponsiveContainer, Layer, Rectangle } from "recharts";

const NAVY = "#003366";

const NodeShape = ({ x, y, width, height, index, payload, onClick }) => (
  <Layer>
    <Rectangle
      x={x} y={y} width={width} height={height}
      fill={payload.dpa_status === "Missing" ? "#D32F2F" : NAVY}
      fillOpacity={0.9} radius={2} style={{ cursor: "pointer" }}
      onClick={() => onClick?.(payload)}
    />
    <text
      x={x + width + 6} y={y + height / 2} textAnchor="start" dominantBaseline="middle"
      fontSize={10} fill="#475569" style={{ cursor: "pointer" }}
      onClick={() => onClick?.(payload)}
      data-testid={`flow-node-${payload.key}`}
    >
      {payload.name}
    </text>
  </Layer>
);

const LinkShape = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload }) => {
  const stroke = payload.missing_dpa ? "#D32F2F" : payload.cross_border ? "#F57C00" : "#CBD5E1";
  return (
    <path
      d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill="none" stroke={stroke} strokeWidth={Math.max(1, linkWidth)}
      strokeOpacity={payload.missing_dpa || payload.cross_border ? 0.55 : 0.3}
    />
  );
};

export const RoPAFlowMap = ({ assessmentId, onNodeSelect }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/ropa/${assessmentId}/flow-map`)
      .then(({ data: res }) => setData(res))
      .catch(() => toast.error("Could not load the data flow map"))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  const chart = useMemo(() => {
    if (!data?.nodes?.length || !data?.edges?.length) return null;
    return {
      nodes: data.nodes.map((n) => ({ ...n })),
      links: data.edges.map((e) => ({ ...e })),
    };
  }, [data]);

  if (loading) return <Skeleton className="h-[420px] rounded-xl" data-testid="ropa-flowmap-loading" />;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ropa-flow-map">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Data flow map</h2>
          <p className="text-xs text-slate-500 mt-1">
            {(data?.columns || []).join(" → ")} · click any node to filter the register
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-[11px]" data-testid="ropa-flowmap-legend">
          <span className="flex items-center gap-1.5"><span className="w-6 h-1 rounded bg-[#CBD5E1]" /> Domestic path</span>
          <span className="flex items-center gap-1.5"><span className="w-6 h-1 rounded bg-[#F57C00]" /> Cross-border (Rule 15)</span>
          <span className="flex items-center gap-1.5"><span className="w-6 h-1 rounded bg-[#D32F2F]" /> Processor without a DPA (S.8(2))</span>
        </div>
      </div>

      {!chart ? (
        <div className="py-16 text-center text-sm text-slate-500" data-testid="ropa-flowmap-empty">
          Link systems and processors to your activities to see the flow map.
        </div>
      ) : (
        <div style={{ height: Math.max(360, chart.nodes.length * 34) }}>
          <ResponsiveContainer width="100%" height="100%">
            <Sankey
              data={chart}
              nodeWidth={12}
              nodePadding={18}
              margin={{ top: 10, right: 170, bottom: 10, left: 10 }}
              node={<NodeShape onClick={onNodeSelect} />}
              link={<LinkShape />}
            >
              <Tooltip
                formatter={(value) => [`${value} activity path(s)`, "Weight"]}
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
              />
            </Sankey>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
